<template>
  <v-container
    grid-list-md
  >
    <v-layout
      justify-center
      wrap
    >
      <v-flex
        md8
      >
        <MaterialCard
          color="green"
          full-width
        >
          <v-data-table
            :headers="headers"
            :items="users"
            hide-actions
            no-data-text="ユーザーの登録がありません。"
          >
            <template
              slot="headerCell"
              slot-scope="{ header }"
            >
              <span
                class="subheading font-weight-light text--darken-3"
                v-text="header.text"
              />
            </template>
            <template
              slot="items"
              slot-scope="{ item }"
            >
              <tr
                :active="item.id === selected.id"
                @click="onUserSelected(item)"
              >
                <td>
                  {{ item.userName }}
                </td>
                <td>
                  {{ item.companyName }}
                </td>
                <td>
                  {{ item.areaName }}
                </td>
                <td>
                  {{ item.roleName }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </MaterialCard>
      </v-flex>
      <v-flex
        column
        md4
        xs3
        mt-0
      >
        <BlackBtn
          class="mb-2"
          style="align-self: flex-end;"
          @click="onUserSelected(newUserDefault())"
        >
          <v-icon left>
            mdi-account-plus
          </v-icon>
          ユーザーを追加
        </BlackBtn>
        <UserDetail
          :selected="selected"
          :loading="detailLoading"
          :role-select="roleSelect"
          :http-errors="httpErrors"
          @clear-error="clearErrors"
          @register="onRegister"
          @user-deleted="refetchAndUpdate(true)"
        />
      </v-flex>
    </v-layout>
  </v-container>
</template>
<script>
import { mapState, mapMutations } from 'vuex'
import { api } from '@/api'
import { onError } from '@/utils/error-handler'
import { UserDetail } from './components'
import { BlackBtn } from '@/components/ui'

export default {
  components: {
    UserDetail,
    BlackBtn
  },
  async beforeRouteEnter (to, from, next) {
    try {
      const data = await search()
      next(vm => {
        vm.init(data)
      })
    } catch (e) {
      onError(next, e)
    }
  },
  data: () => ({
    headers: [
      { sortable: false, text: '氏名', value: 'userName' },
      { sortable: false, text: '会社名', value: 'companyName' },
      { sortable: false, text: '担当エリア', value: 'areaName' },
      { sortable: false, text: '役割', value: 'roleName' }
    ],
    users: [],
    roles: [],
    selected: {},
    detailLoading: false,
    httpErrors: {
      loginId: []
    },
    newUserDefault: () => ({
      id: 'new',
      userName: '',
      loginId: '',
      groupId: null,
      roleId: null,
      remarks: ''
    })
  }),
  computed: {
    ...mapState('groups', ['group']),
    roleSelect () {
      return this.roles.filter(r => r.id <= 2).map(r => ({
        value: r.id,
        text: r.name
      }))
    }
  },
  methods: {
    ...mapMutations('app', ['setGlobalSnackBarText']),
    init (data, selectFirstUser = false) {
      this.users = []

      // 会社
      const companyNames = {}
      const areaNames = {}
      const queue = [this.group]

      while (queue.length > 0) {
        const g = queue.shift()
        switch (g.type) {
          case 'company':
          case 'main_company':
          case 'partner_company':
            companyNames[g.id] = g.name
            break
          case 'area':
            areaNames[g.id] = g.name
            break
        }
        if (g.children) {
          queue.push(...g.children)
        }
      }
      // 役割
      const roleNames = {}
      for (const r of data.roles) {
        roleNames[r.id] = r.name
        this.roles.push(r)
      }

      for (const u of data.users) {
        const user = {
          id: u.id,
          userName: u.name,
          loginId: u.loginId,
          companyName: companyNames[u.groupId],
          groupId: u.groupId,
          roleId: u.roleId,
          roleName: roleNames[u.roleId],
          areaId: u.deliveryGroupId,
          areaName: areaNames[u.deliveryGroupId],
          remarks: u.remarks || ''
        }
        this.users.push(user)
      }

      if (selectFirstUser || (this.users.length && !this.selected.id)) {
        this.selected = this.users.length
          ? this.users[0]
          : {}
      }
    },
    onUserSelected (item) {
      this.selected = item
    },
    async onRegister (data) {
      this.detailLoading = true
      let message
      try {
        if (data.id === 'new') {
          const { id } = await api.createUser({
            name: data.userName,
            loginId: data.loginId,
            groupId: data.groupId,
            deliveryGroupId: data.areaId,
            roleId: data.roleId,
            password: data.password,
            remarks: data.remarks
          })
          data.id = id
          message = `${data.userName}さんを作成しました。`
        } else {
          await api.updateUser({
            name: data.userName,
            loginId: data.loginId,
            groupId: data.groupId,
            deliveryGroupId: data.areaId,
            roleId: data.roleId,
            remarks: data.remarks
          }, data.id)
          message = `${data.userName}さんの情報を更新しました。`
        }

        await this.refetchAndUpdate()

        this.setGlobalSnackBarText(message)
        this.selected = this.users.find(user => user.id === data.id)
      } catch (e) {
        if (e.status === 400 && e.response?.body?.errorTitle === 'user not updated') {
          // ログインユーザー被りエラー
          this.httpErrors.loginId.push('既に使用されています。')
          this.selected = { ...data }
        } else {
          throw e
        }
      } finally {
        this.detailLoading = false
      }
    },
    async refetchAndUpdate (selectFirstUser = false) {
      const updatedData = await search()
      this.init(updatedData, selectFirstUser)
    },
    clearErrors (field) {
      this.httpErrors[field] = []
    }
  }
}

export const search = async () => {
  const response = await api.searchUsers({ _with: 'user-role' })

  return {
    users: response.users.filter(u => u.loginId !== 'admin'),
    roles: response.options.userRoles
  }
}
</script>
